import React from "react";
import { localePath } from "@/i18n/localePath";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ScrollReveal } from "./ScrollEffects";

const FloatingElement: React.FC<{ emoji: string; x: string; y: string; delay: number; duration: number }> = ({
  emoji, x, y, delay, duration,
}) => (
  <motion.span
    className="absolute text-sm pointer-events-none select-none"
    style={{ left: x, top: y, opacity: 0.1 }}
    animate={{ y: [0, -30, 0], opacity: [0.06, 0.14, 0.06] }}
    transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
  >
    {emoji}
  </motion.span>
);

export const CTASection: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  

  return (
    <section
      id="cta"
      className="py-16 sm:py-24 px-4 relative overflow-hidden"
      style={{
        background: "radial-gradient(ellipse 80% 70% at 50% 50%, hsl(35 80% 92% / 0.6) 0%, hsl(var(--background)) 70%)",
      }}
    >
      <FloatingElement emoji="🐾" x="10%" y="20%" delay={0} duration={18} />
      <FloatingElement emoji="❤️" x="85%" y="30%" delay={3} duration={16} />
      <FloatingElement emoji="🐾" x="75%" y="70%" delay={6} duration={20} />
      <FloatingElement emoji="❤️" x="20%" y="75%" delay={2} duration={15} />

      <ScrollReveal className="max-w-2xl mx-auto text-center relative z-10">
        <motion.div
          className="text-7xl sm:text-8xl mb-8"
          initial={{ scale: 0, rotate: -30 }}
          whileInView={{ scale: 1, rotate: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
        >
          🐕
        </motion.div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-5 leading-tight">
          {t('cta.title')}
        </h2>
        <p className="text-muted-foreground text-lg mb-10 max-w-md mx-auto">
          {t('cta.subtitle')}
        </p>
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button
            onClick={() => navigate(localePath("/auth", i18n.language))}
            size="lg"
            className="rounded-2xl h-16 px-14 text-lg font-bold"
            style={{ boxShadow: "0 8px 40px hsl(var(--primary) / 0.25)" }}
          >
            {t('cta.button')}
          </Button>
        </motion.div>
        <motion.p
          className="text-xs text-muted-foreground/50 mt-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          {(t as any)('cta.social_proof', { count: '2,400' })}
        </motion.p>
      </ScrollReveal>
    </section>
  );
};
